/**
 * NULLUM :: App root con routing.  Sprint 1.5b.
 *
 * Boot logic:
 *   - se store ha gia' sigil + uid -> /inbox
 *   - se localStorage ha blob PIN cifrato -> /pin-unlock
 *   - altrimenti -> /welcome
 *
 * Nessun autoLogin che legge mnemonica in chiaro: la mnemonica in chiaro
 * non e' piu' persistita da nessuna parte.
 */

import { useEffect, useState } from 'react';
import {
  BrowserRouter, Routes, Route, Navigate,
} from 'react-router-dom';
import WelcomeScreen from './screens/WelcomeScreen';
import PinUnlockScreen from './screens/PinUnlockScreen';
import PinSetupScreen from './screens/PinSetupScreen';
import RemoteRestoreScreen from './screens/RemoteRestoreScreen';
import RecoverScreen from './screens/RecoverScreen';
import ChangePinScreen from './screens/ChangePinScreen';
import SetPassphraseScreen from './screens/SetPassphraseScreen';
import WipeScreen from './screens/WipeScreen';

import InviteScreen from './screens/InviteScreen';
import GenerateScreen from './screens/GenerateScreen';
import MnemonicScreen from './screens/MnemonicScreen';
import VerifyScreen from './screens/VerifyScreen';
import SigilScreen from './screens/SigilScreen';
import InboxScreen from './screens/InboxScreen';
import ConversationsScreen from './screens/ConversationsScreen';
import ChatScreen from './screens/ChatScreen';
import NewChatScreen from './screens/NewChatScreen';
import InviteCreateScreen from './screens/InviteCreateScreen';
import WhitelistScreen from './screens/WhitelistScreen';
import InfoScreen from './screens/InfoScreen';
import RoomsScreen from './screens/RoomsScreen';
import RoomCreateScreen from './screens/RoomCreateScreen';
import RoomChatScreen from './screens/RoomChatScreen';
import RoomJoinScreen from './screens/RoomJoinScreen';
import InstallGuideScreen from './screens/InstallGuideScreen';
import PremiumScreen from './screens/PremiumScreen';
import SecurityScreen from './screens/SecurityScreen';
import TermsScreen from './screens/TermsScreen';
import SettingsScreen from './screens/SettingsScreen';
import InfoHubScreen from './screens/InfoHubScreen';
import TorScreen from './screens/TorScreen';
import TuonoScreen from './screens/TuonoScreen';
import AccountSelectorScreen from './screens/AccountSelectorScreen';
import SealedScreen from './screens/SealedScreen';
import SealedChatScreen from './screens/SealedChatScreen';

import { hasLocalBackup, hasMultipleAccounts } from './lib/pinCrypto';
import { useStore } from './lib/store';
import { setFsIdentity } from './lib/fsStore';
import { hasDisposableSession, hydrateDisposableSession, pulisciAllAvvio, abortUsaeGetta } from './lib/disposableIdentity';
import InstallPrompt from './components/InstallPrompt';
import { isNativeApp } from './lib/platform';
import TosGate from './components/TosGate';

function HomeRoute() {
  const sigil = useStore((s) => s.sigil);
  const uid = useStore((s) => s.uid);
  const mnemonic = useStore((s) => s.mnemonic);

  // Logged-in session in memory: go straight to inbox
  if (sigil && uid && mnemonic) return <Navigate to="/inbox" replace />;
  // Più identità su questo device: scegli quale sbloccare
  if (hasMultipleAccounts()) return <Navigate to="/accounts" replace />;
  // Encrypted backup present on this device: ask for PIN
  if (hasLocalBackup()) return <Navigate to="/pin-unlock" replace />;
  // No session, no local backup: fresh start
  return <Navigate to="/welcome" replace />;
}

function useBootDelay() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 50);
    return () => clearTimeout(t);
  }, []);
  return ready;
}

export default function App() {
  const ready = useBootDelay();
  // Multi-identità: lo stato FS (ratchet/prekey in IndexedDB) è namespaced per
  // sigil. Qui agganciamo l'identità attiva ad OGNI cambio di sigil (login/switch).
  const activeSigil = useStore((s) => s.sigil);
  useEffect(() => { setFsIdentity(activeSigil || ''); }, [activeSigil]);

  // SESSIONE SICURA (usa-e-getta sotto Tor): dopo il reload di ingresso in
  // modalità Tor, riattiva l'usa-e-getta e idrata lo store PRIMA del routing, così
  // NON passi dal login del tuo account principale. Se non c'è sessione, pulisce
  // eventuali usa-e-getta residue dal catalogo locale (app killata).
  const [secureBooting, setSecureBooting] = useState(false);
  const [secureChecked, setSecureChecked] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      const disp = hasDisposableSession();
      const hasSigil = !!useStore.getState().sigil;
      console.log('[USAEGETTA] boot gate: disposableSession=' + disp + ' storeSigil=' + hasSigil);
      if (disp && !hasSigil) {
        setSecureBooting(true);
        try {
          await hydrateDisposableSession();
        } catch (e) {
          // Fallimento: pulisci la sessione usa-e-getta (niente loop/loader bloccato)
          // e cadi sul login normale. Il sigil creato scade lato server (TTL).
          console.warn('[USAEGETTA] hydrate FALLITA code=' + (e?.code || '?') + ' msg=' + (e?.message || e));
          try { abortUsaeGetta(); } catch { /* no-op */ }
        }
      } else {
        try { pulisciAllAvvio(); } catch { /* no-op */ }
      }
      if (alive) { setSecureBooting(false); setSecureChecked(true); }
    })();
    return () => { alive = false; };
  }, []);

  if (!ready || secureBooting || !secureChecked) {
    return (
      <div className="min-h-screen bg-black text-ink-dim font-mono flex items-center justify-center text-xs">
        {secureBooting ? '🧅 sessione sicura…' : 'loading...'}
      </div>
    );
  }

  return (
    <>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/wipe" element={<WipeScreen />} />

        {/* Login flows */}
        <Route path="/accounts" element={<AccountSelectorScreen />} />
        <Route path="/pin-unlock" element={<PinUnlockScreen />} />
        <Route path="/pin-setup" element={<PinSetupScreen />} />
        <Route path="/restore" element={<RemoteRestoreScreen />} />
        <Route path="/recover" element={<RecoverScreen />} />
        <Route path="/change-pin" element={<ChangePinScreen />} />
        <Route path="/set-passphrase" element={<SetPassphraseScreen />} />

        {/* Onboarding nuovo utente */}
        <Route path="/i" element={<InviteScreen />} />
        <Route path="/i/:token" element={<InviteScreen />} />
        <Route path="/generate" element={<GenerateScreen />} />
        <Route path="/mnemonic" element={<MnemonicScreen />} />
        <Route path="/verify" element={<VerifyScreen />} />
        <Route path="/sigil" element={<SigilScreen />} />

        {/* App principale */}
        <Route path="/inbox" element={<InboxScreen />} />
        <Route path="/conversazioni" element={<ConversationsScreen />} />
        {/* ROOM (gruppi effimeri) — gated dal feature-flag (founder per ora) */}
        <Route path="/rooms" element={<RoomsScreen />} />
        <Route path="/room/new" element={<RoomCreateScreen />} />
        <Route path="/room/:roomId" element={<RoomChatScreen />} />
        <Route path="/room-join" element={<RoomJoinScreen />} />
        <Route path="/room-join/:token" element={<RoomJoinScreen />} />
        <Route path="/chat/:chatId" element={<ChatScreen />} />
        <Route path="/new" element={<NewChatScreen />} />
        <Route path="/invite" element={<InviteCreateScreen />} />
        <Route path="/whitelist" element={<WhitelistScreen />} />
        <Route path="/info" element={<InfoScreen />} />
        <Route path="/install" element={<InstallGuideScreen />} />
        <Route path="/premium" element={<PremiumScreen />} />
        <Route path="/sicurezza" element={<SecurityScreen />} />
        <Route path="/termini" element={<TermsScreen />} />
        <Route path="/impostazioni" element={<SettingsScreen />} />
        <Route path="/tor" element={<TorScreen />} />
        <Route path="/tuono" element={<TuonoScreen />} />
        <Route path="/sealed" element={<SealedScreen />} />
        <Route path="/sealed-chat/:peer" element={<SealedChatScreen />} />
        <Route path="/info-hub" element={<InfoHubScreen />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isNativeApp() && <InstallPrompt />}
      <TosGate />
    </BrowserRouter>
    </>
  );
}
